import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { passwordSchema } from "@/lib/validation";

export function AuthForm() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const ONBOARDING_FLAG = "st_needs_onboarding";

  const clearOnboardingFlag = () => {
    try {
      localStorage.removeItem(ONBOARDING_FLAG);
    } catch {
      // ignore
    }
  };

  const needsOnboarding = () => {
    try {
      return localStorage.getItem(ONBOARDING_FLAG) === "1";
    } catch {
      return false;
    }
  };

  // Only enforce onboarding when explicitly flagged (e.g. OAuth sign-in)
  const routeAfterAuth = async (userId: string) => {
    // Check if user is admin first
    const { data: roleData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .maybeSingle();

    const isAdmin = roleData?.role === "admin";

    if (isAdmin) {
      clearOnboardingFlag();
      navigate("/admin");
      return;
    }

    if (!needsOnboarding()) {
      navigate("/dashboard");
      return;
    }

    const { data: profile } = await supabase
      .from("profiles")
      .select("business_name, phone")
      .eq("id", userId)
      .single();

    if (!profile?.business_name || !profile?.phone) {
      navigate("/onboarding");
      return;
    }

    clearOnboardingFlag();
    navigate("/dashboard");
  };

  // Redirect if already authenticated + show OAuth errors (if any)
  useEffect(() => {
    // If the OAuth provider redirected back with an error, show it before we lose the URL.
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get("error");
    const oauthErrorDescription = params.get("error_description");

    if (oauthError || oauthErrorDescription) {
      const message = decodeURIComponent(oauthErrorDescription || oauthError || "");
      toast({
        title: "Google sign-in failed",
        description: message || "Please try again.",
        variant: "destructive",
      });

      // Clean up query params so refresh doesn't keep re-toasting.
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    let cancelled = false;

    const code = params.get("code");

    const tryRouteIfSession = async () => {
      // If we returned from Google with an OAuth code, explicitly exchange it for a session.
      // (Some environments don’t auto-detect and we end up stuck on /auth.)
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          toast({
            title: "Google sign-in failed",
            description: error.message,
            variant: "destructive",
          });
        }

        // Remove OAuth params after exchanging.
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!cancelled && session?.user) {
        setSessionEmail(session.user.email ?? null);
        console.info("[Auth] session detected", {
          userId: session.user.id,
          email: session.user.email,
        });
        routeAfterAuth(session.user.id);
      } else {
        console.info("[Auth] no session yet");
      }
    };

    // Listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setTimeout(() => {
          routeAfterAuth(session.user.id);
        }, 0);
      }
    });

    // THEN check session
    tryRouteIfSession();

    // In some OAuth flows the session lands a moment later; re-check once.
    const retryTimer = window.setTimeout(() => {
      tryRouteIfSession();
    }, 600);

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
      subscription.unsubscribe();
    };
  }, [navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password on signup
    if (!isLogin) {
      const passwordValidation = passwordSchema.safeParse(password);
      if (!passwordValidation.success) {
        toast({
          title: "Invalid password",
          description: passwordValidation.error.errors[0].message,
          variant: "destructive",
        });
        return;
      }
    }

    setLoading(true);

    try {
      if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;

        toast({
          title: "Welcome back!",
          description: "You've successfully signed in.",
        });

        routeAfterAuth(data.user!.id);
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/dashboard`,
          },
        });
        if (error) throw error;

        toast({
          title: "Account created!",
          description: "Please check your email to verify your account.",
        });
        
        // Redirect to verify-email page with the email as a query param
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider: "google") => {
    try {
      // OAuth doesn’t collect profile info up-front; require onboarding once.
      try {
        localStorage.setItem(ONBOARDING_FLAG, "1");
      } catch {
        // ignore
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          // Redirect back to /auth so we can display OAuth errors instead of losing them
          // to the ProtectedRoute redirect.
          redirectTo: `${window.location.origin}/auth`,
        },
      });
      if (error) throw error;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast({
        title: "Check your email",
        description: "We've sent you a password reset link.",
      });
      setShowResetDialog(false);
      setResetEmail("");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send reset email",
        variant: "destructive",
      });
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-secondary/20 to-background p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            SimpleTrackr
          </CardTitle>
          <CardDescription className="text-center">
            {isLogin ? "Sign in to your account" : "Create a new account"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sessionEmail && (
            <div className="mb-4 rounded-lg border bg-secondary/30 p-3">
              <p className="text-sm text-foreground">
                You’re already signed in as <span className="font-medium">{sessionEmail}</span>.
              </p>
              <div className="mt-3 flex gap-2">
                <Button type="button" className="flex-1" onClick={() => navigate("/dashboard")}
                >
                  Continue to app
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={async () => {
                    await supabase.auth.signOut();
                    setSessionEmail(null);
                    toast({ title: "Signed out" });
                  }}
                >
                  Sign out
                </Button>
              </div>
            </div>
          )}
          <div className="space-y-4">
            <Button type="button" variant="outline" className="w-full" onClick={() => handleOAuthSignIn("google")}>
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Continue with Google
            </Button>


            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLogin ? "Sign In" : "Sign Up"}
            </Button>
          </form>

          {isLogin && (
            <div className="mt-4 text-center">
              <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
                <DialogTrigger asChild>
                  <button type="button" className="text-sm text-primary hover:underline">
                    Forgot password?
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Reset your password</DialogTitle>
                    <DialogDescription>
                      Enter your email address and we'll send you a link to reset your password.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handlePasswordReset} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="reset-email">Email</Label>
                      <Input
                        id="reset-email"
                        type="email"
                        placeholder="you@example.com"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={resetLoading}>
                      {resetLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Send reset link
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}

          <div className="mt-4 text-center text-sm">
            {isLogin ? (
              <button type="button" onClick={() => navigate("/signup")} className="text-primary hover:underline">
                Need an account? Sign up
              </button>
            ) : (
              <button type="button" onClick={() => setIsLogin(!isLogin)} className="text-primary hover:underline">
                Already have an account? Sign in
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
